/**
 * 绑卡支付编排：创建支付单、驱动渠道 3DS 挑战，并向 SDK 调用方汇总结果。
 */
import { PublishPaymentMethodItem } from '@web-publish-id/payment-shared/apis/types/payment'
import type { PaymentComponent } from './payment'

import type { PaymentComponentCore } from '../core'
import { PaymentComponentCreateBindCardOptions, PaymentComponentDropinHandlePayload } from '../types'
import {
  getPaymentUserBindInfoList,
  createPaymentDropinOrder,
  cancelPaymentUserBindInfo,
} from '@web-publish-id/payment-shared/apis/payment'
import { bizApiWrapper } from '@web-publish-id/shared/http'
import { PAYMENT_COM_SDK_VERSION } from '../const/sdk'
import { errorToJson } from '@web-publish-id/shared/utils/error'
import { isNeed3DS2Challenge, isOrderFailed } from '@web-publish-id/payment-shared/utils/orderStatus'
import { createPayermaxDropin } from '@web-publish-id/payment-shared/payments/payermaxDropin'
import { createChecusDropin } from '@web-publish-id/payment-shared/payments/checusDropin'
import { createAdyenDropIn } from '@web-publish-id/payment-shared/payments/adyenDropIn'

export class PaymentComponentBindCard {
  core: PaymentComponentCore
  paymentInstance: PaymentComponent
  methodConfig: PublishPaymentMethodItem
  options: PaymentComponentCreateBindCardOptions

  constructor(
    paymentInstance: PaymentComponent,
    methodConfig: PublishPaymentMethodItem,
    options: PaymentComponentCreateBindCardOptions,
  ) {
    this.paymentInstance = paymentInstance
    this.methodConfig = methodConfig
    this.options = options
    this.core = paymentInstance.core
  }

  /**
   * 获取卡列表
   */
  async getCardList() {
    const { sys_order_id } = this.paymentInstance.config
    const { pay_type } = this.paymentInstance.orderInfo
    const res = await bizApiWrapper(
      getPaymentUserBindInfoList({
        channel_type: this.methodConfig.payment_channel_type,
        sys_order_id,
        payment_method_type: 'Card',
        business_type: pay_type!,
      }),
    )

    return res
  }

  /**
   * 批量取消绑定卡
   */
  async cancelCards(instrument_ids: string[]) {
    const res = await bizApiWrapper(
      cancelPaymentUserBindInfo({
        instrument_ids,
      }),
    )

    return res
  }

  /**
   *
   * @param instrument_id 卡信息id
   * @returns
   */
  async createOrder(instrument_id: string) {
    const payload: PaymentComponentDropinHandlePayload = {
      orderInfo: this.paymentInstance.orderInfo,
      paymentMethod: this.methodConfig,
    }

    try {
      const result = await createPaymentDropinOrder({
        payment_channel_type: this.methodConfig.payment_channel_type,
        payment_method_id: this.methodConfig.payment_method_id,
        sys_order_id: this.paymentInstance.config.sys_order_id,
        sdk_version: PAYMENT_COM_SDK_VERSION,
        os: 1,
        instrument_id,
        // 测试控制，是否使用动态3ds
        use_dynamic_3ds: !!localStorage.getItem('__use_3ds__'),
      })

      if (!result?.payment_id) {
        throw new Error('Create order failed')
      }

      // 支付失败
      if (isOrderFailed(this.methodConfig, result.result_data)) {
        throw new Error(result.result_data.msg)
      }

      // 需要3DS2挑战
      if (isNeed3DS2Challenge(this.methodConfig, result.result_data)) {
        await this.create3DS2Challenge(result.result_data, payload, result.payment_id)
      }
    } catch (error) {
      console.error(error)
      payload.failed = errorToJson(error)
      this.options.onFailed?.(payload)

      return
    }

    this.options.onCompleted?.(payload)
  }

  /**
   * 3DS2挑战
   */
  async create3DS2Challenge(resultData: any, payload: PaymentComponentDropinHandlePayload, paymentId: string) {
    switch (this.methodConfig.payment_channel_type) {
      case 1:
        throw new Error('3DS2 challenge not supported for Adyen')

      case 4:
        return await this.createPayermaxAction(resultData, payload, paymentId)

      case 10:
        return await this.createChecusAction(resultData, payload)

      default:
        break
    }
  }

  // 创建adyen action
  async createAdyenAction(resultData: any) {
    const { adyenConfig } = this.core
    if (!adyenConfig) {
      throw new Error('Adyen config is not set')
    }

    const { language } = this.core.configuration

    return new Promise((resolve, reject) => {
      const adyenDropIn = createAdyenDropIn({
        clientKey: adyenConfig.client_key,
        locale: language!,
        orderInfo: this.paymentInstance.orderInfo,

        onCompleted: () => {
          resolve(true)
        },
        onFailed: (error) => {
          reject(error)
        },
        onError: (error) => reject(error),
      })

      adyenDropIn.setupFromAction(`#${this.options.actionContainerId}`, resultData.action)
    })
  }

  // 创建payermax action
  async createPayermaxAction(resultData: any, payload: PaymentComponentDropinHandlePayload, paymentId: string) {
    const { language } = this.core.configuration

    return new Promise((resolve, reject) => {
      const payermaxDropin = createPayermaxDropin({
        locale: language!,
        orderInfo: this.paymentInstance.orderInfo,
        paymentMethod: this.methodConfig,
        isSandbox: import.meta.env.MODE !== 'production',
        callbackUrl: this.options.callback_url || window.location.href,
        getPaymentId: () => paymentId,
        onCompleted: () => {
          resolve(true)
        },
        onFailed: (error) => {
          reject(error)
        },
        onError: (error) => {
          reject(error)
        },
      })

      payermaxDropin.setupFromAction(resultData.data)
    })
  }

  // 创建checus action(机械复刻 createPayermaxAction,仅 dropin 工厂改为 createChecusDropin)
  async createChecusAction(resultData: any, payload: PaymentComponentDropinHandlePayload) {
    const { language } = this.core.configuration

    return new Promise((resolve, reject) => {
      const checusDropin = createChecusDropin({
        locale: language!,
        orderInfo: this.paymentInstance.orderInfo,
        paymentMethod: this.methodConfig,
        isSandbox: import.meta.env.MODE !== 'production',
        callbackUrl: this.options.callback_url || window.location.href,
        onCompleted: () => {
          resolve(true)
        },
        onFailed: (error) => {
          reject(error)
        },
        onError: (error) => {
          reject(error)
        },
      })

      checusDropin.setupFromAction(resultData.data)
    })
  }
}
